import {
	LoginRequestDto,
	LoginSSORequestDto,
	LogoutReason,
	LogoutRequestDto,
	LogoutUserDto,
} from "@api-types/user/LoginRequestDto";
import {
	LoginResponseDto,
	LoginUserMatchingResponseDto,
	RedirectLoginResponseDto,
} from "@api-types/user/LoginResponseDto";
import { UserMatchingRequestDto } from "@api-types/user/UserMatchingRequestDto";

import {
	UserTypes,
	getAgencyId,
	getAgencyKey,
	getSecondaryUser,
	getShopId,
	getUserToken,
	isTypeOf,
} from "@utils";
import { LoginType } from "@utils/common/loginTypeEnum";
import { MitAuthDto } from "@api-types/integrations/MitAuthDto";
import { BankIdAuthDto } from "@api-types/integrations";
import UserApiService from "@api-services/user/UserApiService";
import AgencyUserAccountRelationApiService from "@api-services/user/AgencyUserAccountRelationApiService";
import { errorCodesMap } from "./ErrorCodesMap";
import {
	validateAcceptGdprConsentResponse,
	validateMatchingDataResponse,
	validateRefreshUserResponse,
	validateUserLoginResponse,
	validateUserMatchConfirmationResponse,
} from "@utils/auth";
import { logger } from "@utils/logger/logger";
import { ContextOptions } from "@utils/api-config/types";
import { LoginErrorResponseModel, UserAccount } from "@data-types";
import { UserAccountType } from "@common/constants";
import { RequestContextType } from "@utils/api-config/RequestContext";
import { createApiRequestOptions } from "@utils/api-config/createApiRequestOptions";
import PlatformGpLoginApiService from "@api-services/platform/PlatformGpLoginApiService";
import UserAccountsApiService from "@api-services/user/UserAccountsApiService";

export default class UserService {
	static async loginUser(
		userData:
			| LoginRequestDto
			| LoginSSORequestDto
			| MitAuthDto
			| BankIdAuthDto,
		loginType: LoginType = LoginType.DEFAULT
	): Promise<LoginResponseDto | null> {
		try {
			let response: LoginResponseDto | RedirectLoginResponseDto;

			switch (loginType) {
				case LoginType.MITID:
					response = await UserApiService.loginUserWithMit(
						userData as MitAuthDto
					);
					break;
				case LoginType.BANKID:
					response = await UserApiService.loginUserWithBankId(
						userData as BankIdAuthDto
					);
					break;
				case LoginType.SSO:
					response = await UserApiService.ssoLogin(
						userData as LoginSSORequestDto
					);
					break;
				default:
					response = await UserApiService.loginUser(
						userData as LoginRequestDto
					);
			}

			const isResponseValid = validateUserLoginResponse(response);

			if (!isResponseValid) {
				return null;
			}

			if (isTypeOf<RedirectLoginResponseDto>(response, "redirectUrl")) {
				window.location.replace(response.redirectUrl);
				return null;
			}

			return response;
		} catch (error) {
			if (error == null || error?.message == "Unauthorized") {
				throw new Error("USER_UNABLE");
			}

			const errorCode = Number(error?.data?.errorCode);

			const errorInfo = errorCodesMap(error).get(errorCode) || {
				errorMessage: "USER_UNABLE",
			};

			const modelError: LoginErrorResponseModel = {
				errorCode: error?.data?.errorCode,
				message: errorInfo.errorMessage,
				mobileApkLink: errorInfo.mobileApkLink,
				errorFailedAttempts: errorInfo.errorFailedAttempts,
				errorDuration: errorInfo.errorDuration,
				userData: errorInfo.userData,
			};

			throw new LoginErrorResponseModel(modelError);
		}
	}

	static async findMatchingData(): Promise<string[]> {
		try {
			const response =
				await AgencyUserAccountRelationApiService.findMatchingData();

			const isResponseValid = validateMatchingDataResponse(response);

			return isResponseValid ? response : [];
		} catch (error) {
			logger.logError(error);
			return [];
		}
	}

	static async confirmUserMatching(
		userData: UserMatchingRequestDto
	): Promise<LoginUserMatchingResponseDto> {
		try {
			const response = await UserApiService.confirmUserMatching(userData);

			const isResponseValid =
				validateUserMatchConfirmationResponse(response);

			if (!isResponseValid) {
				throw new Error("Response not valid");
			}
			return response;
		} catch (error) {
			const modelError: LoginErrorResponseModel = {
				errorCode: error?.data?.errorCode,
			};
			throw new LoginErrorResponseModel(modelError);
		}
	}

	static async denyUserMatching(): Promise<void> {
		try {
			return await AgencyUserAccountRelationApiService.denyUserMatching();
		} catch (error) {
			const modelError: LoginErrorResponseModel = {
				errorCode: error?.data?.errorCode,
			};

			await App.state.rootStore.userAuthStore.logoutUser();

			throw new LoginErrorResponseModel(modelError);
		}
	}

	static async deactivateUser(
		isTimeoutLogout: boolean = false,
		contextType: RequestContextType
	): Promise<void> {
		try {
			return await PlatformGpLoginApiService.deactivateUser(
				isTimeoutLogout,
				createApiRequestOptions(contextType)
			);
		} catch (error) {
			logger.logError(error);
		}
	}

	static async logoutUser(reasonsObj?: LogoutReason): Promise<void> {
		let requestModel = {} as LogoutRequestDto;
		requestModel.primary = {} as LogoutUserDto;
		const primaryToken = getUserToken();
		requestModel.primary.token = primaryToken ? primaryToken.token : "";
		requestModel.primary.agencyId = getAgencyId();
		requestModel.primary.agencyKey = getAgencyKey();
		requestModel.primary.shopId = getShopId();

		if (getSecondaryUser()) {
			requestModel.secondary = {} as LogoutUserDto;
			const secondaryToken = getUserToken(UserTypes.SECONDARY);
			requestModel.secondary.token = secondaryToken
				? secondaryToken.token
				: "";
			requestModel.secondary.agencyId = getAgencyId(UserTypes.SECONDARY);
			requestModel.secondary.agencyKey = getAgencyKey(
				UserTypes.SECONDARY
			);
			requestModel.secondary.shopId = getShopId(UserTypes.SECONDARY);
		}

		if (reasonsObj) {
			requestModel.primary.isAutomaticLogout =
				reasonsObj.isAutomaticLogout;
			requestModel.primary.isRealityCheckLogout =
				reasonsObj.isRealityCheckLogout;
			requestModel.primary.isTimeoutLogout = reasonsObj.isTimeoutLogout;
		}

		try {
			await UserApiService.logoutUser(requestModel);
		} catch (error) {
			logger.logError(error);
		}
	}

	static async acceptGDPRConsent() {
		try {
			const response = await UserApiService.acceptGDPRConsent();

			const isResponseValid = validateAcceptGdprConsentResponse(response);

			if (!isResponseValid) {
				throw new Error("Response is not valid");
			}
			return response;
		} catch (error) {
			logger.logError(error);
			return false;
		}
	}

	static async refreshUser(options: ContextOptions) {
		try {
			const response = await UserApiService.refreshUser(options);

			const isResponseValid = validateRefreshUserResponse(response);

			if (!isResponseValid) {
				throw new Error("Response is not valid");
			}
			return response;
		} catch (error) {
			logger.logError(error);
			return null;
		}
	}

	static async getUserAccounts(options?: ContextOptions) {
		try {
			const userAccounts =
				await UserAccountsApiService.getUserAccounts(options);

			if (!(userAccounts && userAccounts.item)) {
				return [];
			}
			const allowedUserAccounts = userAccounts.item.filter((ub) =>
				WEBPACK_ALLOWED_ACCOUNTS.includes(ub.accountType.abrv)
			);

			const mappedUserAccounts: UserAccount[] =
				allowedUserAccounts.map<UserAccount>((a) => ({
					id: a.id,
					accountTypeId: a.accountType.id,
					name: a.accountType.name,
					abrv: a.accountType.abrv as UserAccountType,
					balance: a.balance,
					currency: a.currency,
					userId: a.userId,
					isCurrentlyActive: a.isCurrentlyActive,
					displayName: a.displayName,
					shopId: a.shopId,
					agencyId: a.agencyId,
					agencyIdentifier: a.agencyIdentifier,
					isPrimary: a.isPrimary,
					isBettingOnWebWithShopOnlineDisabled:
						a.isBettingOnWebWithShopOnlineDisabled,
					isOrgStructureActive: a.isOrgStructureActive,
					isManuallyDisabled: a.isManuallyDisabled,
					isRelatedUserLocked: a.isRelatedUserLocked,
				}));

			return mappedUserAccounts;
		} catch (e) {
			console.error("Failed to fetch updated user accounts:", e);
			return [];
		}
	}
}
